// Transport for the public shared LOGBOOK-trip view: shareTrip.mjs's sibling,
// on the same terms. The anonymous page never touches AppSync (every table is
// userPool-authed) -- it fetches the share-logbook-trip Lambda's Function URL,
// which validates the unguessable share id and returns a whitelisted
// {logbookType, summary, entries, places, photos}.
//
// shareLogbookTripAvailable() is the deploy gate and it guards MORE than the
// button. The one backend deploy carries the TaskPogoLogbookTripShare model,
// the Lambda and the Function URL together, so the URL's presence in
// amplify_outputs.json IS "the schema is live" -- and AppSync rejects a WHOLE
// query naming a field the branch does not have, so every owner-side call here
// checks it first. (That is why this feature needs no separate published flag
// the way the trip weather email does: that one is reached only through AppSync
// and has no Function URL to notice.)

import outputs from '../amplify_outputs.json';
import {
  deleteLogbookTripShare,
  listLogbookTripSharesForLogbook,
  updateLogbookTripShareKey,
} from './Database.mjs';

function shareLogbookTripBase() {
  return outputs?.custom?.shareLogbookTripFunctionUrl || '';
}

export function shareLogbookTripAvailable() {
  return Boolean(shareLogbookTripBase());
}

// Resolves to {status: 'ok', ...} or {status: 'gone'} (revoked, never issued,
// or the trip no longer exists -- the viewer can't tell and neither can we).
// Throws on network failures and server errors, which the page reports as
// "try again later" rather than "gone".
//
// `photosOk` is carried through deliberately: it is what lets the page tell
// "asked, there are none" from "couldn't ask", the distinction
// `photosByEntry === null` carries on the owner's side.
//
// `summary` is the Lambda's, because the page can no longer compute it: the
// flag that decides which entries count is withheld (see LogbookTypes'
// publicFieldsForType). `null` means "draw no totals line" -- never zero. This
// reshape is a whitelist of its own, so a key the Lambda adds and this omits
// simply never reaches the page.
export async function fetchSharedLogbookTrip(shareId) {
  const base = shareLogbookTripBase();
  if (!base) throw new Error('Trip sharing is not configured.');
  const query = new URLSearchParams({ shareId }).toString();
  const response = await fetch(`${base}?${query}`);
  if (response.status === 404 || response.status === 410) return { status: 'gone' };
  if (!response.ok) throw new Error(`Shared logbook trip request returned ${response.status}`);
  const body = await response.json();
  return {
    status: 'ok',
    logbookName: body?.logbookName ?? '',
    logbookType: body?.logbookType ?? '',
    summary: body?.summary ?? null,
    entries: body?.entries ?? [],
    places: body?.places ?? [],
    photos: body?.photos ?? {},
    photosOk: body?.photosOk === true,
    // Whether this link accepts visitor photos. `=== true` rather than passing the
    // value through: a branch whose share-logbook-trip predates the key answers
    // `undefined`, and the honest reading of that is "no upload control", not a
    // truthy blob. The Lambda has already applied the nullish-means-on rule to the
    // stored column, so this is the answer and not the raw setting.
    uploadsAllowed: body?.uploadsAllowed === true,
  };
}

// The rows a merge onto `newKey` touches, keeper first.
//
// tripShareRowsForMerge's counterpart, split out for the same reason: the
// Combine dialog asks the same question BEFORE the merge -- "will this revoke a
// link?" is only useful while it can still be answered no -- and a second copy
// of the keeper rule in the page would be free to disagree with the one that
// does the deleting.
//
// A row already on `newKey` wins; otherwise the first key in `oldKeys` does,
// and callers pass the trip the user is standing on first, so the link they
// published for THIS trip is the one that keeps working. Deduped by id because
// the same row would otherwise appear twice when newKey is one of oldKeys.
export function logbookTripShareRowsForMerge(rows, oldKeys, newKey) {
  const keys = [newKey, ...(oldKeys || [])];
  return keys
    .flatMap((key) => (rows || []).filter((row) => row?.tripKey === key))
    .filter((row, index, all) => all.findIndex((r) => r?.id === row?.id) === index);
}

// The rows a merge would REVOKE -- everything but the keeper. What the dialog
// warns about, since a revoked link cannot be brought back.
export async function logbookTripSharesLostByMerge(logbookId, oldKeys, newKey) {
  if (!shareLogbookTripAvailable()) return [];
  try {
    const rows = await listLogbookTripSharesForLogbook(logbookId);
    return logbookTripShareRowsForMerge(rows, oldKeys, newKey).slice(1);
  } catch (err) {
    // A failed read must never block the merge: the worst case is a warning
    // the user did not get, and they can still see the link's state afterwards.
    console.error('Could not check this logbook for share links', err);
    return [];
  }
}

// Move the share rows of two trips that have just become one onto `newKey`,
// and repoint one whose legacy 'n:' key a rename has moved.
//
// AT MOST ONE ROW may end up on a key, which is getLogbookTripShareForKey's
// actual behaviour (it answers with items[0]). Two rows on one key is a live
// public URL its owner can no longer see or revoke, so the extras are deleted
// rather than left.
//
// Doing nothing instead would be worse than untidy. A stranded 'g:' key answers
// "no longer available" forever with no way to clean it up; a stranded legacy
// 'n:' key still resolves BY NAME, so whoever holds the old link would silently
// be shown a trip they were never given.
//
// Best-effort: the rename or merge has already landed by the time this runs, so
// a failure here must not turn a completed write into an error the user is
// invited to retry. Returns { repointed, revoked }.
export async function repointLogbookTripShares(logbookId, oldKeys, newKey) {
  if (!shareLogbookTripAvailable()) return { repointed: 0, revoked: 0 };
  const result = { repointed: 0, revoked: 0 };
  try {
    const rows = await listLogbookTripSharesForLogbook(logbookId);
    const matching = logbookTripShareRowsForMerge(rows, oldKeys, newKey);
    if (matching.length === 0) return result;

    const [keep, ...extras] = matching;
    for (const row of extras) {
      await deleteLogbookTripShare(row.id);
      result.revoked += 1;
    }
    if (keep.tripKey !== newKey) {
      await updateLogbookTripShareKey(keep.id, newKey);
      result.repointed += 1;
    }
    return result;
  } catch (err) {
    console.error('Updated the trip but could not update its share links', err);
    return result;
  }
}
